import {
    Button,
    Icon,
    Icons,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    Stack,
    Text,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
    useToast,
} from "@caspeco/casper-ui-library";
import { useAvailableTimes } from "api/api-hooks/use-available-times";
import { useUpdateBooking } from "api/api-hooks/use-update-booking";
import { getEditBookingAvailableTimes } from "components/edit-booking/edit-booking-availability";
import EditContactView from "components/edit-booking/edit-contact-view";
import EditGuestsView from "components/edit-booking/edit-guests-view";
import EditMessageView from "components/edit-booking/edit-message-view";
import EditOptionCard from "components/edit-booking/edit-option-card";
import EditTimeView from "components/edit-booking/edit-time-view";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { error as logError, trackEvent } from "logger/logger";
import { usePageTracking } from "logger/use-page-tracking";
import { clickTrackingService } from "logger/click-tracking";
import {
    PageType,
    BookingFeature,
    pageTrackingService,
    TRACKING_VERSION,
} from "logger/page-tracking";
import { useStepTracking } from "logger/use-step-tracking";
import { TrackingStepName } from "logger/user-tracking";
import queryClient from "query-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ContactInfoForm } from "store/store-types";
import { Contact } from "types/contact";
import { TimeSlot } from "types/time-slot";
import { WebBooking } from "types/web-booking";

interface IEditBookingModal {
    isOpen: boolean;
    onClose: () => void;
    booking: WebBooking;
}

enum EditBookingOptions {
    Guests = "guests",
    Time = "time",
    Contact = "contact",
    Message = "message",
}
const QUERY_INVALIDATION_DELAY_MS = 50;
const EditBookingModal = ({ isOpen, onClose, booking }: IEditBookingModal) => {
    const [selectedOption, setSelectedOption] = useState<EditBookingOptions | null>(null);
    const [guests, setGuests] = useState<number | null>(booking.guests);
    const timesToUpdate = booking.getBookTimes();
    const [time, setTime] = useState<TimeSlot | null>(
        timesToUpdate?.at(0) ? new TimeSlot(timesToUpdate.at(0) as Partial<TimeSlot>) : null
    );
    const update = useUpdateBooking();
    const state = useBookingStateContext();
    const { setContact } = useBookingActionsContext();
    const { showToast } = useToast();
    const { t } = useTranslation();

    // Track edit modal when opened (OLD SYSTEM)
    useStepTracking({
        stepName: TrackingStepName.EditBooking,
        autoTrack: isOpen,
        dependencies: [isOpen],
    });

    // NEW SYSTEM: Track modal open (one event per open)
    usePageTracking({
        pageName: "EditBookingModal",
        pageType: PageType.Modal,
        autoTrack: isOpen,
        dependencies: [isOpen],
        metadata: {
            feature: BookingFeature.Edit,
            bookingGuid: booking.guid,
            originalGuests: booking.guests,
        },
    });

    // Track option selection via click tracking
    useEffect(() => {
        if (selectedOption && isOpen) {
            clickTrackingService.recordClick(
                `edit-option-${selectedOption}`,
                `Edit ${selectedOption}`,
                "button"
            );
        }
    }, [selectedOption, isOpen]);

    const sectionIds = booking.webBookingRules?.map((rule) => rule.sectionId);
    const {
        data: availableTimesData,
        isLoading: isLoadingAvailableTimes,
        isError: isAvailableTimesError,
    } = useAvailableTimes(
        state.date,
        sectionIds,
        // Set unit id only when modal opens to only fetch data when necessary
        isOpen ? booking.unitId.toString() : undefined
    );
    const guestsForAvailability = state.guests ?? booking.guests ?? 0;
    const availableTimes = useMemo(() => {
        if (
            !availableTimesData ||
            isLoadingAvailableTimes ||
            isAvailableTimesError ||
            availableTimesData.length === 0
        ) {
            return [];
        }

        return getEditBookingAvailableTimes(
            availableTimesData[0].clone(),
            booking,
            guestsForAvailability,
            !!state.isBookingRequest
        );
    }, [
        availableTimesData,
        booking,
        guestsForAvailability,
        isAvailableTimesError,
        isLoadingAvailableTimes,
        state.isBookingRequest,
    ]);
    const bookedRuleId = booking.webBookingRules?.at(0);
    const relevantRulesOnSection = availableTimes
        .at(0)
        ?.timeSets.filter(
            (rule) => rule.title === bookedRuleId?.title && !rule.requiresAnyPayment()
        );
    // Find the correct time slot that matches the booking start time for the sharing the same title as the booked rule
    // This ensures that if the time has been moved to another rule with same title, we still get the availableSeats from that rule
    const bookedTimeSlots = relevantRulesOnSection
        ?.flatMap((rule) => rule.times.map((time) => ({ ...time, ruleId: rule.id })))
        .filter((time) => time.start.isSame(booking.start));
    const availableGuestSizes = bookedTimeSlots?.flatMap((time) => time.availableSeats);
    // Map for looking up rule at save
    const guestsToRuleMap = new Map<number, number>(
        bookedTimeSlots?.flatMap((time) => time.availableSeats.map((size) => [size, time.ruleId]))
    );

    const { control, trigger, reset, formState } = useForm<ContactInfoForm>({
        mode: "onBlur",
        defaultValues: { ...booking.contact, message: booking.contact?.message || "" },
    });

    const resetValues = () => {
        setSelectedOption(null);
        setTime(null);
        setGuests(booking.guests);
        setContact?.({ ...booking.contact, message: booking?.message || "" });
        reset();
    };

    const handleOnClose = () => {
        onClose();
        resetValues();
        firstRender.current = true;
    };

    const firstRender = useRef(true);
    useEffect(() => {
        if (isOpen && firstRender.current) {
            firstRender.current = false;
            setGuests(booking.guests);
            setContact?.({ ...booking.contact, message: booking?.message || "" });
        }
    }, [
        booking.contact,
        booking.guests,
        booking?.message,
        booking.webBookingRules,
        isOpen,
        setContact,
        state.contact,
    ]);

    const contact = (state.contact || {}) as Contact;

    const onSave = async () => {
        if (!guests) return;
        let timesToUpdate = booking.getBookTimes();

        let ruleId = time?.ruleId;

        if (selectedOption === EditBookingOptions.Guests) {
            ruleId = guestsToRuleMap.get(guests);
        }

        timesToUpdate = [
            {
                start: time ? time.start : booking.start,
                ruleId: ruleId ? ruleId : timesToUpdate.at(0)?.ruleId || null,
            },
        ];

        await update.mutateAsync(
            {
                state,
                timesToUpdate,
                guests,
                messageOverride: contact.message,
                contact: state.contact,
                isPending: false,
            },
            {
                onSuccess: () => {
                    onClose();

                    // OLD SYSTEM: Keep existing tracking
                    trackEvent("EditBookingSaved", {
                        editType: selectedOption || "unknown",
                    });

                    // NEW SYSTEM: Enhanced tracking
                    const getOriginalValue = () => {
                        switch (selectedOption) {
                            case EditBookingOptions.Guests:
                                return booking.guests.toString();
                            case EditBookingOptions.Time:
                                return booking.start?.format("HH:mm") || "";
                            case EditBookingOptions.Contact:
                            case EditBookingOptions.Message:
                                return JSON.stringify(booking.contact);
                            default:
                                return "";
                        }
                    };

                    const getNewValue = () => {
                        switch (selectedOption) {
                            case EditBookingOptions.Guests:
                                return guests?.toString() || "";
                            case EditBookingOptions.Time:
                                return time?.start?.format("HH:mm") || "";
                            case EditBookingOptions.Contact:
                            case EditBookingOptions.Message:
                                return JSON.stringify(contact);
                            default:
                                return "";
                        }
                    };

                    // NEW SYSTEM: Enhanced tracking with session context
                    const pageContext = pageTrackingService.getCurrentContext();
                    trackEvent("Booking.EditCompleted", {
                        editType: selectedOption || "unknown",
                        bookingGuid: booking.guid,
                        originalValue: getOriginalValue(),
                        newValue: getNewValue(),
                        sessionId: pageContext.sessionId,
                        trackingVersion: TRACKING_VERSION,
                    });

                    showToast({
                        type: "success",
                        description: t("editBooking.changeSaved"),
                        duration: 5000,
                    });
                    if (
                        selectedOption === EditBookingOptions.Guests ||
                        selectedOption === EditBookingOptions.Time
                    ) {
                        setTimeout(() => {
                            void queryClient.invalidateQueries({
                                queryKey: ["availableTimes", booking.start?.format("YYYY-MM-DD")],
                            });
                        }, QUERY_INVALIDATION_DELAY_MS);
                    }
                },
                onError: (error) => {
                    logError("Error updating booking:", error);

                    // NEW SYSTEM: Enhanced error tracking with session context
                    const pageContext = pageTrackingService.getCurrentContext();
                    trackEvent("Booking.EditFailed", {
                        editType: selectedOption || "unknown",
                        bookingGuid: booking.guid,
                        error: error?.message || "Unknown error",
                        errorType: error?.name || "Error",
                        sessionId: pageContext.sessionId,
                        trackingVersion: TRACKING_VERSION,
                    });

                    showToast({
                        type: "error",
                        title: t("editBooking.saveFailedTitle"),
                        description: t("editBooking.saveFailedDescription"),
                        duration: 5000,
                    });
                },
            }
        );
        handleOnClose();
    };

    const editedGuests = booking.guests !== guests && guests !== null;
    const editedTime = time && !time?.start.isSame(booking.start);
    const editedContact = formState.isDirty;
    const isDirty = editedGuests || editedTime || editedContact;
    const isSavedDisabled =
        !selectedOption ||
        !isDirty ||
        (!formState.isValid && selectedOption === EditBookingOptions.Contact);
    return (
        <Modal isOpen={isOpen} onClose={handleOnClose} size="md">
            <ModalHeader
                display="flex"
                justifyContent="space-between"
                p={ThemeSpaceVariable.Medium}
            >
                {selectedOption && (
                    <Button
                        variant="ghost"
                        name={t("back")}
                        onClick={() => resetValues()}
                        data-track-click="edit-booking-back"
                    >
                        <Icon icon={Icons.Back} />
                        {t("editBooking.back")}
                    </Button>
                )}
                {!selectedOption && (
                    <Text fontWeight="medium" fontSize={ThemeFontSizeVariable.XLarge}>
                        {t("editBooking.changePrompt")}
                    </Text>
                )}
                <ModalCloseButton />
            </ModalHeader>
            <ModalBody display="flex" flexDirection="column" p={ThemeSpaceVariable.Medium}>
                {!selectedOption && (
                    <Stack gap={ThemeSpaceVariable.Medium} mb={ThemeSpaceVariable.Medium}>
                        <EditOptionCard
                            title={t("editBooking.bookingTime")}
                            icon={Icons.Time}
                            onClick={() => setSelectedOption(EditBookingOptions.Time)}
                            trackingId="edit-booking-time"
                        />
                        <EditOptionCard
                            title={t("editBooking.guestCount")}
                            icon={Icons.UserGroup}
                            onClick={() => setSelectedOption(EditBookingOptions.Guests)}
                            trackingId="edit-booking-guests"
                        />
                        <EditOptionCard
                            title={t("editBooking.contactInfo")}
                            icon={Icons.User}
                            onClick={() => setSelectedOption(EditBookingOptions.Contact)}
                            trackingId="edit-booking-contact"
                        />
                        <EditOptionCard
                            title={t("editBooking.addMessage")}
                            icon={Icons.Message}
                            onClick={() => setSelectedOption(EditBookingOptions.Message)}
                            trackingId="edit-booking-message"
                        />
                    </Stack>
                )}
                {selectedOption === EditBookingOptions.Time && (
                    <EditTimeView
                        availableTimesSections={availableTimes}
                        selectedTimeSlot={
                            time ?? new TimeSlot(timesToUpdate?.at(0) as Partial<TimeSlot>)
                        }
                        setTime={setTime}
                        isLoading={isLoadingAvailableTimes}
                    />
                )}
                {selectedOption === EditBookingOptions.Guests && (
                    <EditGuestsView
                        guests={guests}
                        setGuests={setGuests}
                        availableGuestSizes={availableGuestSizes ?? []}
                        isLoading={isLoadingAvailableTimes}
                    />
                )}
                {selectedOption === EditBookingOptions.Contact && (
                    <EditContactView contact={contact} control={control} trigger={trigger} />
                )}
                {selectedOption === EditBookingOptions.Message && (
                    <EditMessageView control={control} value={contact.message} />
                )}
            </ModalBody>
            <ModalFooter
                pt={ThemeSpaceVariable.Medium}
                px={ThemeSpaceVariable.Medium}
                pb={ThemeSpaceVariable.Medium}
                display="flex"
                justifyContent="flex-end"
            >
                <Stack direction="row" spacing={ThemeSpaceVariable.Medium}>
                    <Button
                        variant="ghost"
                        size="xl"
                        alignSelf="flex-end"
                        onClick={handleOnClose}
                        data-track-click="cancel-edit"
                    >
                        {t("editBooking.close")}
                    </Button>
                    <Button
                        variant="primary"
                        size="xl"
                        alignSelf="flex-end"
                        isDisabled={isSavedDisabled}
                        isLoading={update.isPending}
                        onClick={() => void onSave()}
                        data-track-click={`save-edit-${selectedOption}`}
                    >
                        {t("editBooking.saveChanges")}
                    </Button>
                </Stack>
            </ModalFooter>
        </Modal>
    );
};

export default EditBookingModal;
